import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ForceGraph2D from 'react-force-graph-2d';
import { trackEvent } from '../lib/analytics';
import './GraphView.css';

// 8-color palette tuned for dark background — brand purple first
const CLUSTER_COLORS = [
  '#8b5cf6', // purple (Reedle brand)
  '#22d3ee', // cyan
  '#34d399', // emerald
  '#fbbf24', // amber
  '#f87171', // red
  '#e879f9', // fuchsia
  '#a3e635', // lime
  '#fb923c', // orange
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function getReadingProgress(article) {
  if (!article.total_blocks || article.total_blocks === 0) return 0;
  const hidden = (article.hidden_block_indices || []).length;
  return Math.min(1, hidden / article.total_blocks);
}

/**
 * Compute k-NN edges from 2D UMAP coords.
 * UMAP preserves local topology, so nearby in 2D ≈ semantically similar.
 * Similarity is approximated as 1 - normalized_distance.
 */
function buildKNNEdges(coords, k = 5) {
  const n = coords.length;
  const edges = [];
  const edgeSet = new Set();

  // Find max Euclidean distance for normalization
  let maxDist = 0;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = Math.sqrt((coords[i].x - coords[j].x) ** 2 + (coords[i].y - coords[j].y) ** 2);
      if (d > maxDist) maxDist = d;
    }
  }
  if (maxDist === 0) maxDist = 1;

  for (let i = 0; i < n; i++) {
    const dists = [];
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const d = Math.sqrt((coords[i].x - coords[j].x) ** 2 + (coords[i].y - coords[j].y) ** 2);
      dists.push({ j, d });
    }
    dists.sort((a, b) => a.d - b.d);
    dists.slice(0, k).forEach(({ j, d }) => {
      const key = i < j ? `${i}-${j}` : `${j}-${i}`;
      if (!edgeSet.has(key)) {
        edgeSet.add(key);
        edges.push({
          source: coords[i].id,
          target: coords[j].id,
          similarity: 1 - d / maxDist,
        });
      }
    });
  }
  return edges;
}

// ── Component ─────────────────────────────────────────────────────────────────

const GraphView = ({ universeData, articleMap, archivedIds = new Set() }) => {
  const { t } = useTranslation('reader');
  const navigate = useNavigate();
  const fgRef = useRef(null);
  const hoveredRef = useRef(null);
  const containerRef = useRef(null);

  const [hoveredNode, setHoveredNode] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  // Container resize observer
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const { coords, kmeansAssign, kmeansLabels } = universeData || {};

  // Build graph data from universeData
  const graphData = useMemo(() => {
    if (!coords?.length) return { nodes: [], links: [] };

    // k-NN edges from 2D UMAP proximity
    const links = buildKNNEdges(coords, 5);

    // Nodes: seed initial positions from UMAP (physics can refine)
    // Scale [-1,1] → ~[-150,150] for a good initial spread
    const SCALE = 150;
    const nodes = coords.map((c) => {
      const art = articleMap[c.id] || {};
      return {
        id: c.id,
        title: art.title || t('common.untitled'),
        cover_url: art.cover_url || null,
        progress: getReadingProgress(art),
        colorIndex: kmeansAssign?.[c.id] ?? 0,
        isArchived: archivedIds.has(c.id),
        url: art.url,
        x: c.x * SCALE,
        y: c.y * SCALE,
      };
    });

    return { nodes, links };
  }, [coords, kmeansAssign, articleMap, archivedIds, t]);

  const clusterLabels = kmeansLabels || {};

  // Node canvas drawing
  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const isHovered = hoveredRef.current?.id === node.id;
    const isArchived = node.isArchived;
    const color = CLUSTER_COLORS[node.colorIndex % CLUSTER_COLORS.length];
    // Archived nodes render slightly smaller and more transparent
    const r = Math.max(isArchived ? 3 : 4, (isArchived ? 4 : 5) / Math.sqrt(globalScale));

    ctx.save();

    // Glow
    if (isHovered) {
      ctx.shadowColor = color;
      ctx.shadowBlur = 12;
    }

    // Progress arc ring (outer) — only for active articles
    if (!isArchived && node.progress > 0.02) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, r + 2.5 / globalScale, -Math.PI / 2,
        -Math.PI / 2 + node.progress * Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5 / globalScale;
      ctx.stroke();
    }

    // Core circle — archived nodes are more faded
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
    ctx.fillStyle = isHovered ? color : (isArchived ? `${color}55` : `${color}99`);
    ctx.fill();

    ctx.shadowBlur = 0;

    // Label — show on hover or high zoom
    if (isHovered || globalScale >= 3) {
      const label = node.title?.length > 22 ? node.title.slice(0, 22) + '…' : node.title;
      const fontSize = Math.max(8, 11 / globalScale);
      ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Inter', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      const textWidth = ctx.measureText(label).width;
      const pad = 3 / globalScale;
      ctx.fillStyle = 'rgba(10,10,15,0.8)';
      ctx.fillRect(
        node.x - textWidth / 2 - pad,
        node.y + r + 3 / globalScale,
        textWidth + pad * 2,
        fontSize + pad * 2,
      );

      ctx.fillStyle = '#e0e0e0';
      ctx.fillText(label, node.x, node.y + r + 3 / globalScale + pad);
    }

    ctx.restore();
  }, []);

  const nodePointerAreaPaint = useCallback((node, color, ctx, globalScale) => {
    const r = Math.max(4, 5 / Math.sqrt(globalScale));
    ctx.beginPath();
    ctx.arc(node.x, node.y, r + 4, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }, []);

  const handleNodeHover = useCallback((node) => {
    hoveredRef.current = node || null;
    setHoveredNode(node || null);
    document.body.style.cursor = node ? 'pointer' : 'default';
  }, []);

  const handleNodeClick = useCallback((node) => {
    if (node?.id) {
      trackEvent('graph_node_clicked', { article_id: node.id });
      navigate(`/article/${node.id}`);
    }
  }, [navigate]);

  // Draw cluster labels on canvas (follows pan/zoom automatically)
  const onRenderFramePost = useCallback((ctx, globalScale) => {
    const labelEntries = Object.entries(clusterLabels);
    if (!graphData.nodes.length || !labelEntries.length) return;

    // Compute live centroids per colorIndex from current node x/y
    const sums = {}, counts = {};
    graphData.nodes.forEach((n) => {
      if (n.x === undefined || n.y === undefined) return;
      const key = String(n.colorIndex);
      sums[key] = sums[key] || [0, 0];
      counts[key] = (counts[key] || 0) + 1;
      sums[key][0] += n.x;
      sums[key][1] += n.y;
    });

    const fontSize = Math.max(7, 9 / globalScale);
    const pad = 4 / globalScale;
    ctx.save();
    ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, 'Inter', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    labelEntries.forEach(([cid, label]) => {
      const sum = sums[String(cid)];
      const cnt = counts[String(cid)];
      if (!sum || !cnt) return;
      const cx = sum[0] / cnt;
      const cy = sum[1] / cnt;
      const text = label.toUpperCase();
      const tw = ctx.measureText(text).width;
      // Dark background pill — readable on any page background color
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fillRect(cx - tw / 2 - pad, cy - fontSize / 2 - pad, tw + pad * 2, fontSize + pad * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.75)';
      ctx.fillText(text, cx, cy);
    });
    ctx.restore();
  }, [graphData.nodes, clusterLabels]);

  // Tune d3-force link distance so similar nodes cluster tighter
  useEffect(() => {
    if (!graphData.nodes.length) return;
    const timer = setTimeout(() => {
      const linkForce = fgRef.current?.d3Force('link');
      if (!linkForce) return;
      // Higher similarity → shorter distance → nodes pulled closer
      linkForce.distance((link) => Math.max(20, (1 - link.similarity) * 180));
      linkForce.strength((link) => link.similarity * 0.7);
      fgRef.current?.d3ReheatSimulation();
    }, 100);
    return () => clearTimeout(timer);
  }, [graphData.nodes.length]);

  const linkColor = useCallback((link) => {
    if (link.similarity < 0.65) return 'rgba(0,0,0,0)';
    return `rgba(160,160,220,${Math.min(0.65, link.similarity * 0.9)})`;
  }, []);

  if (!coords?.length) {
    return (
      <div className="graph-empty">
        <h2>{t('universe.empty.noEmbeddingsTitle')}</h2>
        <p>{t('universe.empty.noEmbeddingsBody')}</p>
      </div>
    );
  }

  return (
    <div className="graph-view" ref={containerRef}>
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        width={dimensions.width}
        height={dimensions.height}
        backgroundColor="rgba(0,0,0,0)"
        onRenderFramePre={(ctx) => {
          ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        }}
        nodeCanvasObject={nodeCanvasObject}
        nodePointerAreaPaint={nodePointerAreaPaint}
        onNodeHover={handleNodeHover}
        onNodeClick={handleNodeClick}
        linkColor={linkColor}
        linkWidth={(l) => (l.similarity >= 0.65 ? Math.max(0.4, l.similarity * 1.5) : 0)}
        linkDirectionalParticles={(l) => (l.similarity >= 0.65 ? 2 : 0)}
        linkDirectionalParticleSpeed={(l) => l.similarity * 0.004}
        linkDirectionalParticleWidth={1.5}
        linkDirectionalParticleColor={linkColor}
        onRenderFramePost={onRenderFramePost}
        cooldownTicks={200}
        d3AlphaDecay={0.015}
        d3VelocityDecay={0.25}
      />

      {/* Hover card */}
      {hoveredNode && (
        <div className="graph-hover-card">
          {hoveredNode.cover_url && (
            <img src={hoveredNode.cover_url} alt="" className="graph-hover-cover" />
          )}
          <div className="graph-hover-info">
            <p className="graph-hover-title">{hoveredNode.title}</p>
            {hoveredNode.progress > 0 && (
              <div className="graph-hover-progress">
                <div
                  className="graph-hover-progress-fill"
                  style={{
                    width: `${Math.round(hoveredNode.progress * 100)}%`,
                    background: CLUSTER_COLORS[hoveredNode.colorIndex % CLUSTER_COLORS.length],
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GraphView;
